import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticateMutation } from '../../../api/mutations/authenticate';
import { useAuthenticateWithSmsOtpMutation } from '../../../api/mutations/authenticateWithSmsOTP';
import LoginContent from '../../../layouts/LoginLayout/LoginContent';
import useHandleError from '../../../utilities/useHandleError';
import OTPForm, { type OTPFormValues } from '../OTPForm';
import { type FormValues } from './AuthenticateStep';
import { type ActionHandlers } from './index';

export type SmsOTPStepProps = { actions: ActionHandlers; token: string; values: FormValues };

const SmsOTPStep = ({ actions, token, values }: SmsOTPStepProps) => {
    const { t } = useTranslation(['signInPage']);
    const [authenticate] = useAuthenticateMutation();
    const [mutation] = useAuthenticateWithSmsOtpMutation();

    const onSubmit = useHandleError(
        async ({ code }: OTPFormValues) => {
            message.loading({
                type: 'loading',
                content: t('signInPage:smsOtpStep.submittingMessage'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await mutation({ variables: { token, code: code.join('') } }).finally(() => {
                message.destroy('primary');
            });

            switch (data.authenticateWithSmsOTP.__typename) {
                case 'AuthenticationSuccessful':
                    return actions.completeAuthentication(
                        data.authenticateWithSmsOTP.token,
                        data.authenticateWithSmsOTP.user
                    );

                case 'AuthenticationRequiresNewPassword':
                    return actions.moveToPasswordExpired(data.authenticateWithSmsOTP.token);

                default:
                    // not supported
                    return undefined;
            }
        },
        [actions, mutation, t, token]
    );

    const resendCode = useCallback(() => {
        authenticate({ variables: values })
            .then(({ data }) => {
                switch (data.authenticate.__typename) {
                    case 'AuthenticationRequiresSmsOTP':
                        return actions.moveToSmsOTP(data.authenticate.token, values);

                    default:
                        // not supported
                        return undefined;
                }
            })
            .catch(() => message.error(t('otpPage:errors.failedToResend')));
    }, [actions, authenticate, t, values]);

    return (
        <LoginContent description={t('signInPage:smsOtpStep.description')} title={t('signInPage:smsOtpStep.title')}>
            <OTPForm handleSubmit={onSubmit} resendCode={resendCode} />
        </LoginContent>
    );
};

export default SmsOTPStep;
